/**
 * buildClaimWizardSteps — assembles the SCHMIDT two-phase claim wizard step list:
 * a curated, Sparte-light CUSTOMER phase (nearly identical for every Sparte) plus
 * a Sparte-family-specific BROKER phase (kfz / haftpflicht / sach). This is the
 * schmidt-only counterpart to `journey/buildJourneySteps.js` (which drives the
 * generic single-phase journey and is left untouched).
 *
 * Field descriptor objects are pulled BY NAME (verbatim, by reference) from the
 * selected formdef, so the name-keyed MDDForm engine (validation / empty-model /
 * transforms / submit) is unaffected — only the step grouping changes. Any formdef
 * field not placed by a template lands in a hidden "leftover" broker step so the
 * flattened model stays complete and no value is ever dropped.
 *
 * The concrete step list is built for the claim's current family. Because `sparte`
 * selects the formdef and re-classification re-mounts the form (ClaimForm keys the
 * renderer by sparte), there is no need for runtime `when` predicates here.
 */

import { familyOf, isKasko, isBetrieb } from "./familyMap.js";

// ── Curated customer phase (shared across families) ──────────────────────────
// `fields` entries are either a field `name` (pulled from the formdef) or an
// inline control spec (a presentational control with no formdef descriptor).
const CUSTOMER_TEMPLATE = [
  {
    key: "stammdaten",
    railLabel: "Ihre Daten",
    label: "Ihre Daten prüfen",
    description:
      "Ein kurzer Blick auf Ihre hinterlegten Daten. So vermeiden wir spätere Rückfragen.",
    maxWidth: 760,
    fields: [
      { control: "claim_masterdata_card", cols: 12 },
      "ansprechpartner_vorname",
      "ansprechpartner_name",
      "ansprechpartner_telefon",
      "ansprechpartner_mobil",
      "ansprechpartner_email",
    ],
  },
  {
    key: "vertrag",
    railLabel: "Vertrag",
    label: "Welcher Vertrag ist betroffen?",
    description:
      "In Ihrem Bereich sehe ich diese Verträge. Wissen Sie, welcher betroffen ist? Falls nicht, ist das überhaupt kein Problem.",
    maxWidth: 900,
    fields: [{ control: "claim_contract_card", cols: 12 }],
  },
  {
    key: "fahrzeug",
    railLabel: "Fahrzeug",
    label: "Welches Fahrzeug ist betroffen?",
    description:
      "Bitte wählen Sie das betroffene Fahrzeug – so ordnen wir den Schaden dem richtigen Vertrag zu.",
    maxWidth: 760,
    family: "kfz", // only built for the kfz family
    requiredNames: ["fahrzeug_kennzeichen"],
    fields: [{ control: "claim_vehicle_card", cols: 12 }],
  },
  {
    key: "schadenort",
    railLabel: "Schadenort",
    label: "Wo ist der Schaden entstanden?",
    description: "Geben Sie an, an welchem Ort sich der Schaden ereignet hat.",
    maxWidth: 680,
    requiredNames: ["schaden_ort"],
    fields: [
      {
        control: "claim_location",
        lockNames: [
          "schaden_ort",
          "schaden_ort_abweichend",
          "schaden_ort_strasse",
          "schaden_ort_plz",
          "schaden_ort_stadt",
        ],
      },
    ],
  },
  {
    key: "hergang",
    railLabel: "Schadenhergang",
    label: "Was ist passiert?",
    description:
      "Schildern Sie bitte, wann und wie es zu dem Schaden kam – möglichst Ihre eigenen Wahrnehmungen.",
    maxWidth: 680,
    fields: ["schaden_datum", "schaden_uhrzeit", "schaden_schilderung"],
  },
  {
    key: "dokumente",
    railLabel: "Fotos & Dokumente",
    label: "Fotos & Dokumente",
    description:
      "Haben Sie Bilder oder Dokumente zum Schaden? Das hilft uns sehr bei der Einschätzung.",
    maxWidth: 720,
    fields: [
      // Required upload unless the customer marks that they have no media yet.
      {
        name: "sonstiges_unterlagen",
        valid: "getValue('keine_medien') === true || isNotEmpty(item)",
      },
      {
        control: "chiptoggle",
        name: "keine_medien",
        label: "Ich habe aktuell keine Medien",
        cols: 12,
      },
    ],
  },
  {
    key: "schadenhoehe",
    railLabel: "Schadenhöhe",
    label: "Schadenhöhe schätzen",
    description:
      "Können Sie den Schaden grob beziffern? Eine ungefähre Schätzung genügt.",
    maxWidth: 480,
    fields: [
      // Estimate is required unless the customer opts out via the checkbox.
      {
        name: "schaden_hoehe",
        label: "Geschätzte Höhe (Neuwert)",
        valid:
          "getValue('schaden_hoehe_unbekannt') === true || isNotEmpty(item)",
        cols: 6,
      },
      {
        control: "chiptoggle",
        name: "schaden_hoehe_unbekannt",
        label: "Kann ich nicht einschätzen",
        cols: 12,
      },
    ],
  },
  {
    key: "weitere",
    railLabel: "Weitere Infos",
    label: "Weitere Informationen",
    description:
      "Möchten Sie uns sonst noch etwas mitteilen, das für die Bearbeitung wichtig sein könnte?",
    maxWidth: 680,
    fields: [
      {
        control: "textarea",
        name: "schaden_weitere_infos",
        label: "Anmerkungen (optional)",
        rows: 4,
        cols: 12,
      },
    ],
  },
  {
    key: "bestaetigung",
    railLabel: "Bestätigung",
    label: "Angaben bestätigen",
    description:
      "Fast geschafft. Bitte bestätigen Sie zum Abschluss noch Ihre Angaben.",
    maxWidth: 680,
    requireCheck: "alles_korrekt",
    fields: [{ control: "claim_confirm", lockNames: ["alles_korrekt"] }],
  },
];

// ── Broker phase per family ──────────────────────────────────────────────────
const EINORDNUNG = {
  key: "einordnung",
  railLabel: "Einordnung",
  label: "Schaden einordnen",
  description:
    "Die Meldung des Kunden ist eingegangen. Ordnen Sie den Schaden zunächst Sparte und Schadenart zu – der Kunde war sich unsicher.",
  maxWidth: 820,
  requiredNames: ["schaden_ursache"],
  fields: [{ control: "claim_einordnung", cols: 12 }],
};

// Shared inline field groups reused by every broker family so the recap and the
// backend treat them uniformly. `bank_nachgereicht` and all COMM_FIELDS names
// are dummy-only fields (no formdef descriptor); the bank account fields pull
// their descriptors from paymentSection but override the reveal to key off
// `bank_nachgereicht` instead of the API-account select.
const BANK_FIELDS = [
  {
    control: "chiptoggle",
    name: "bank_nachgereicht",
    label: "Bankverbindung wird nachgereicht",
    cols: 12,
  },
  {
    name: "auszahlung_schaden_kontoinhaber",
    label: "Kontoinhaber",
    cols: 12,
    hidden: "isEqualOther('bank_nachgereicht', true)",
  },
  {
    name: "auszahlung_schaden_iban",
    label: "IBAN",
    cols: 7,
    hidden: "isEqualOther('bank_nachgereicht', true)",
  },
  {
    name: "auszahlung_schaden_bank",
    label: "Kreditinstitut",
    cols: 5,
    hidden: "isEqualOther('bank_nachgereicht', true)",
  },
];

const COMM_FIELDS = [
  {
    control: "segmented",
    name: "kontaktweg",
    label: "Gewünschte Kontaktaufnahme bei Rückfragen",
    options: ["Telefon", "E-Mail", "sonstiges"],
    cols: 12,
  },
  {
    control: "input",
    name: "kontaktdaten",
    label: "Kontaktdaten (E-Mail / Telefon)",
    cols: 6,
  },
  {
    control: "input",
    name: "erreichbarkeit",
    label: "Erreichbarkeit (bei Telefon)",
    cols: 6,
  },
  {
    control: "segmented",
    name: "schriftverkehr",
    label: "Schriftverkehr",
    options: ["an Versicherungsnehmer", "an Versicherungsmakler"],
    cols: 12,
  },
  {
    control: "chiptoggle",
    name: "info_regulierung",
    label: "Info über Regulierung erbeten",
    cols: 12,
  },
];

// Conditional reveal predicates (kfz Fahrer step) — mirror the dummy's logic
// rather than the formdef's older gating (which keyed off fahrer_identisch_halter).
const LENKER_ABWEICHEND = "isNotEqualOther('fahrzeug_lenker_typ', 'abweichend')";
const KEINE_FAHRERLAUBNIS =
  "isNotEqualOther('fahrer_fahrerlaubnis_vorhanden', true)";

// Regulierungsempfehlung: pulled by name so the shared regulierungSection
// descriptor drives it (3 options incl. "Abwehr" → dummyized to segmented; the
// Betrag / Auszahlung-an fields self-reveal on "Regulierung in bestimmter Höhe").
const REGULIERUNG_STEP = {
  key: "regulierung",
  railLabel: "Regulierung",
  label: "Regulierungsempfehlung",
  description: "Ihre Regulierungsempfehlung an den Versicherer.",
  maxWidth: 720,
  fields: [
    "regulierung_empfehlung",
    "regulierung_betrag",
    "regulierung_auszahlung_an",
    "regulierung_bemerkungen",
  ],
};

const RECAP_STEP = {
  key: "recap",
  railLabel: "Prüfen & Senden",
  label: "Prüfen & an Versicherer senden",
  description:
    "Alle Pflichtfelder sind erfasst. Bitte prüfen Sie die vollständige Schadenmeldung vor dem Versand.",
  maxWidth: 900,
  fields: [{ control: "claim_recap", cols: 12 }],
};

const BROKER_TEMPLATES = {
  // kfz / haftpflicht are functions of the reclassification context so the
  // Kasko-only (vorschäden, Direktabrechnung) and Betrieb-only (betriebsrolle)
  // fields track the chosen Sparte, exactly like the dummy's isKasko/isBetrieb.
  kfz: ({ isKasko: kasko }) => [
    EINORDNUNG,
    {
      key: "fahrzeug_vertrag",
      railLabel: "Fahrzeug",
      label: "Fahrzeug & Vertrag",
      description: "Erfassen Sie Vertrag, Fahrzeug und Finanzierung.",
      maxWidth: 820,
      fields: [
        { name: "vertragsnummer", label: "Versicherungsnummer" },
        { name: "fahrzeug_kennzeichen", label: "Versichertes Kfz (Kennzeichen)" },
        { name: "gesellschaft", label: "Versicherungsunternehmen" },
        {
          control: "input",
          name: "schaden_nummer_vu",
          label: "Schadennummer des Versicherers",
          cols: 12,
        },
        "fahrzeug_finanzierung",
        { name: "fahrzeug_km", label: "Kilometerstand (km)" },
        kasko && { name: "schaden_vorschaeden", label: "Vorschäden am Fahrzeug?" },
        kasko && {
          name: "schaden_vorschaeden_repariert",
          label: "Vorschäden wurden repariert",
          hidden: "isNotEqualOther('schaden_vorschaeden', true)",
        },
      ].filter(Boolean),
    },
    {
      key: "fahrer",
      railLabel: "Fahrer",
      label: "Fahrer & Fahrerlaubnis",
      description:
        "Wer fuhr das Fahrzeug und lag eine gültige Fahrerlaubnis vor?",
      maxWidth: 820,
      fields: [
        { name: "fahrzeug_lenker_typ", label: "Fahrzeuglenker zum Schadenzeitpunkt" },
        { name: "fahrt_genehmigt", label: "War die Fahrt vom Versicherungsnehmer genehmigt?" },
        { name: "fahrer_vorname", cols: 6, hidden: LENKER_ABWEICHEND },
        { name: "fahrer_name", cols: 6, hidden: LENKER_ABWEICHEND },
        { name: "fahrer_strasse", cols: 8, hidden: LENKER_ABWEICHEND },
        { name: "fahrer_hausnummer", cols: 4, hidden: LENKER_ABWEICHEND },
        { name: "fahrer_plz", cols: 4, hidden: LENKER_ABWEICHEND },
        { name: "fahrer_ort", cols: 8, hidden: LENKER_ABWEICHEND },
        {
          name: "fahrer_fahrerlaubnis_vorhanden",
          label: "Gültige Fahrerlaubnis für das gefahrene Kfz?",
        },
        {
          name: "fahrer_fuehrerschein_klasse",
          label: "Führerscheinklasse",
          cols: 6,
          hidden: KEINE_FAHRERLAUBNIS,
        },
        {
          name: "fahrer_fuehrerschein_ausstellungsdatum",
          label: "seit wann?",
          cols: 6,
          hidden: KEINE_FAHRERLAUBNIS,
        },
        { name: "fahrer_fahrerlaubnis_behoerde", label: "ausstellende Behörde", cols: 6 },
        { name: "fahrer_fahrerlaubnis_auflagen", label: "Einschränkungen / Auflagen", cols: 6 },
        {
          name: "polizei_alkohol_drogen",
          label:
            "Stand der/die Fahrzeuglenker/in unter Alkohol- oder Drogeneinfluss?",
          hidden: null, // un-gate: the dummy asks this in the Fahrer step
        },
      ],
    },
    {
      key: "beteiligte",
      railLabel: "Beteiligte",
      label: "Anhänger & Verursacher",
      description:
        "Angaben zu einem Anhänger und zur Frage, wer den Schaden verursacht hat.",
      maxWidth: 820,
      fields: [
        { name: "anhaenger_kennzeichen", label: "Anhänger – amtliches Kennzeichen", cols: 4 },
        { name: "anhaenger_gesellschaft", label: "versichert bei (Gesellschaft)", cols: 4 },
        { name: "anhaenger_vertragsnummer", label: "Vertragsnummer", cols: 4 },
        {
          name: "schaden_verursacher",
          label: "Wer hat den Schaden verursacht? (Name und Anschrift)",
        },
      ],
    },
    {
      key: "anspruchsteller",
      railLabel: "Anspruchsteller",
      label: "Geschädigter / Anspruchsteller",
      description: "Erfassen Sie den (vermutlichen) Anspruchsteller.",
      maxWidth: 820,
      fields: [
        "geschaedigte_person_vorname",
        "geschaedigte_person_name",
        "geschaedigte_person_strasse",
        "geschaedigte_person_hausnummer",
        "geschaedigte_person_land",
        "geschaedigte_person_plz",
        "geschaedigte_person_ort",
        "geschaedigte_person_telefon",
        "geschaedigte_person_email",
        { name: "vorsteuerabzugsberechtigt", label: "Vorsteuerabzugsberechtigt?" },
      ],
    },
    {
      key: "fahrzeugschaden",
      railLabel: "Fahrzeugschaden",
      label: "Sach- & Personenschaden",
      description:
        "Schäden am gegnerischen Kfz sowie etwaige Personenschäden.",
      maxWidth: 820,
      fields: [
        { control: "grouplabel", label: "Liegen Nachweise vor?", cols: 12 },
        { control: "chiptoggle", name: "nachweis_fotos", label: "Fotos anbei", cols: 4 },
        {
          control: "chiptoggle",
          name: "nachweis_kostenvoranschlag",
          label: "Kostenvoranschlag anbei",
          cols: 4,
        },
        {
          control: "chiptoggle",
          name: "nachweis_rechnung",
          label: "Rechnung anbei",
          cols: 4,
        },
        { name: "geschaedigte_person_kennzeichen", label: "Kennzeichen", cols: 4 },
        { name: "geschaedigte_person_fahrzeug", label: "Fabrikat, Typ und Baujahr", cols: 8 },
        { name: "geschaedigte_person_art_beschaedigung", label: "Art der Beschädigung" },
        { name: "geschaedigte_person_fahrzeug_standort", label: "Aktueller Standort des Kfz", cols: 8 },
        { name: "geschaedigte_person_schadenhoehe", label: "Geschätzte Schadenhöhe", cols: 4 },
        kasko && {
          name: "direktabrechnung",
          label: "Direktabrechnung mit Reparaturfirma",
        },
        { name: "schaden_personenschaden", label: "Liegt ein Personenschaden vor?" },
        "geschaedigte_person_verletzung_beschreibung",
        "geschaedigte_person_arztbericht",
      ].filter(Boolean),
    },
    {
      key: "behoerden",
      railLabel: "Behörden & Recht",
      label: "Behörden & rechtliche Verfahren",
      description: "Polizeiliche und rechtliche Umstände des Unfalls.",
      maxWidth: 820,
      fields: [
        { name: "polizei_aufgenommen", label: "Polizeiliches Protokoll aufgenommen?" },
        { name: "polizei_dienststelle", label: "Behörde / Dienststelle", cols: 6 },
        { name: "polizei_tagebuchnummer", label: "Aktenzeichen", cols: 6 },
        {
          name: "ermittlungsverfahren",
          label: "Ermittlungs- oder Strafverfahren eingeleitet?",
        },
        "ermittlungsverfahren_behoerde",
        "ermittlungsverfahren_aktenzeichen",
        { name: "fahrerflucht", label: "Liegt Fahrerflucht vor?" },
        "fahrerflucht_details",
        { name: "schaden_abtretungserklärung", label: "Abtretungserklärung liegt vor?" },
      ],
    },
    {
      key: "bank",
      railLabel: "Bank & Abwicklung",
      label: "Bankverbindung & Abwicklung",
      description: "Bankverbindung und gewünschte Kommunikation festlegen.",
      maxWidth: 820,
      fields: [...BANK_FIELDS, ...COMM_FIELDS],
    },
    REGULIERUNG_STEP,
    RECAP_STEP,
  ],
  haftpflicht: ({ isBetrieb: betrieb }) => [
    EINORDNUNG,
    {
      key: "anspruchsteller",
      railLabel: "Anspruchsteller",
      label: "Geschädigter / Anspruchsteller",
      description:
        "Erfassen Sie den (vermutlichen) Anspruchsteller und dessen Bankverbindung.",
      maxWidth: 820,
      fields: [
        "geschaedigte_person_vorname",
        "geschaedigte_person_name",
        "geschaedigte_person_geburtsdatum",
        "geschaedigte_person_strasse",
        "geschaedigte_person_hausnummer",
        "geschaedigte_person_land",
        "geschaedigte_person_plz",
        "geschaedigte_person_ort",
        "geschaedigte_person_telefon",
        "geschaedigte_person_email",
        { name: "vorsteuerabzugsberechtigt", label: "Vorsteuerabzugsberechtigt?" },
        ...BANK_FIELDS,
      ],
    },
    {
      key: "verhaeltnis",
      railLabel: "Umstände",
      label: "Verhältnis & Verschulden",
      description: "Angaben zum Verhältnis der Parteien und zum Verschulden.",
      maxWidth: 820,
      fields: [
        betrieb
          ? { name: "betriebsrolle", label: "Schadenverursacher – im Betrieb tätig als" }
          : {
              name: "verwandtschaftsverhaeltnis",
              label: "Verwandtschaftsverhältnis mit dem Geschädigten?",
            },
        !betrieb && "verwandtschaftsverhaeltnis_welches",
        !betrieb && {
          name: "haeusliche_gemeinschaft",
          label: "Leben Sie mit dem Geschädigten in häuslicher Gemeinschaft?",
        },
        { name: "verschulden_vn", label: "Liegt ein Verschulden des Versicherungsnehmers vor?" },
        "verschulden_vn_beschreibung",
        {
          name: "anderweitige_haftpflicht",
          label: "Anderweitige Haftpflichtversicherung des Verursachers?",
        },
        "anderweitige_haftpflicht_versicherer",
        "anderweitige_haftpflicht_vsnr",
      ].filter(Boolean),
    },
    {
      key: "sachschaden",
      railLabel: "Sachschaden",
      label: "Sachschaden",
      description: "Angaben zu den beschädigten Sachen.",
      maxWidth: 820,
      fields: [
        { name: "schaden_beschaedigte_sache", label: "Was ist beschädigt?" },
        { control: "grouplabel", label: "Liegen Nachweise vor?", cols: 12 },
        { control: "chiptoggle", name: "nachweis_fotos", label: "Fotos anbei", cols: 6 },
        {
          control: "chiptoggle",
          name: "nachweis_rechnung",
          label: "Anschaffungsrechnung anbei",
          cols: 6,
        },
        { name: "anschaffungsjahr", label: "Anschaffungsjahr ca.", cols: 4 },
        { name: "anschaffungspreis", label: "Anschaffungspreis ca.", cols: 4 },
        {
          control: "numberinput",
          name: "schaden_hoehe_bewertung",
          label: "Schadenhöhe ca.",
          unit_type: "currency",
          unit: "€",
          allow_negative: false,
          cols: 4,
        },
        {
          name: "miet_leihverhaeltnis",
          label: "Waren die beschädigten Teile gemietet / gepachtet / geliehen?",
        },
        {
          name: "gewerbliche_nutzung",
          label: "Gewerbliche/berufliche Tätigkeit an den beschädigten Teilen?",
        },
        "schaden_besichtigung_adresse",
      ],
    },
    {
      key: "personenschaden",
      railLabel: "Personenschaden",
      label: "Personenschaden",
      description: "Angaben bei Verletzungen von Personen.",
      maxWidth: 820,
      fields: [
        { name: "schaden_personenschaden", label: "Liegt ein Personenschaden vor?" },
        "geschaedigte_person_beruf_arbeitgeber",
        "geschaedigte_person_krankenkasse",
        { name: "geschaedigte_person_arbeitsunfall", label: "Arbeitsunfall?" },
        { name: "geschaedigte_person_wegunfall", label: "Wegunfall?" },
        "geschaedigte_person_arzt_krankenhause",
      ],
    },
    {
      key: "zeugen_behoerden",
      railLabel: "Zeugen & Behörden",
      label: "Zeugen & Behörden",
      description: "Zeugen und behördliche Verfahren.",
      maxWidth: 820,
      fields: [
        { name: "schaden_zeugen", label: "Können Sie Zeugen benennen? (Name und Anschrift)" },
        { name: "polizei_aufgenommen", label: "Polizeiliches Protokoll aufgenommen?" },
        { name: "polizei_dienststelle", label: "Behörde / Dienststelle", cols: 6 },
        { name: "polizei_tagebuchnummer", label: "Aktenzeichen", cols: 6 },
      ],
    },
    {
      key: "abwicklung",
      railLabel: "Bank & Abwicklung",
      label: "Abwicklung & Kommunikation",
      description: "Legen Sie Kommunikation und Schriftverkehr fest.",
      maxWidth: 820,
      fields: [...COMM_FIELDS],
    },
    REGULIERUNG_STEP,
    RECAP_STEP,
  ],
  sach: [
    EINORDNUNG,
    {
      key: "versicherer",
      railLabel: "Versicherer",
      label: "Vertrag & Versicherer",
      description: "Erfassen Sie die Vertrags- und Versichererdaten.",
      maxWidth: 760,
      fields: [
        { name: "vertragsnummer", label: "Versicherungsnummer" },
        { name: "gesellschaft", label: "Versicherungsunternehmen" },
        {
          control: "input",
          name: "schaden_nummer_vu",
          label: "Schadennummer des Versicherers",
          cols: 12,
        },
      ],
    },
    {
      key: "beschaedigung",
      railLabel: "Beschädigung",
      label: "Beschädigte Sachen",
      description:
        "Formalisieren Sie die beschädigten Sachen und bewerten Sie den Schaden.",
      maxWidth: 820,
      fields: [
        {
          name: "schaden_beschaedigte_sache",
          label: "Art, Umfang & Bezeichnung der beschädigten Sachen",
        },
        {
          control: "segmented",
          name: "reparatur_sinnvoll",
          label: "Erscheint eine Reparatur wirtschaftlich sinnvoll?",
          options: ["wirtschaftlich sinnvoll", "nicht sinnvoll", "unklar"],
          cols: 12,
        },
        { control: "grouplabel", label: "Liegen Nachweise vor?", cols: 12 },
        { control: "chiptoggle", name: "nachweis_fotos", label: "Fotos anbei", cols: 6 },
        {
          control: "chiptoggle",
          name: "nachweis_rechnung",
          label: "Anschaffungsrechnung anbei",
          cols: 6,
        },
        {
          control: "numberinput",
          name: "schaden_hoehe_bewertung",
          label: "Geschätzte Schadenhöhe (Neuwert)",
          unit_type: "currency",
          unit: "€",
          allow_negative: false,
          cols: 4,
        },
      ],
    },
    {
      key: "weitere_umstaende",
      railLabel: "Weitere Umstände",
      label: "Weitere Umstände",
      description: "Erfassen Sie weitere versicherungsrelevante Umstände.",
      maxWidth: 820,
      fields: [
        {
          control: "segmented",
          name: "anderw_vsschutz",
          boolean: true,
          label: "Besteht anderweitiger Versicherungsschutz?",
          cols: 12,
        },
        {
          control: "input",
          name: "anderw_versicherer",
          label: "Versicherer",
          cols: 6,
          hidden: "isNotEqualOther('anderw_vsschutz', true)",
        },
        {
          control: "input",
          name: "anderw_vsnr",
          label: "VS-Nr.",
          cols: 6,
          hidden: "isNotEqualOther('anderw_vsschutz', true)",
        },
        {
          control: "segmented",
          name: "verursacher_bekannt",
          boolean: true,
          label: "Ist der Schadenverursacher bekannt?",
          cols: 12,
        },
        {
          control: "textarea",
          name: "verursacher_name_anschrift",
          label: "Name & Anschrift des Verursachers",
          cols: 12,
          hidden: "isNotEqualOther('verursacher_bekannt', true)",
        },
        {
          control: "segmented",
          name: "zeugen_vorhanden",
          boolean: true,
          label: "Können Zeugen benannt werden?",
          cols: 12,
        },
        {
          control: "input",
          name: "zeugen_name",
          label: "Name",
          cols: 5,
          hidden: "isNotEqualOther('zeugen_vorhanden', true)",
        },
        {
          control: "input",
          name: "zeugen_anschrift",
          label: "Anschrift",
          cols: 7,
          hidden: "isNotEqualOther('zeugen_vorhanden', true)",
        },
        {
          name: "polizei_aufgenommen",
          label: "Wurde ein polizeiliches Protokoll aufgenommen?",
        },
        { name: "polizei_dienststelle", label: "Behörde / Dienststelle" },
        { name: "polizei_tagebuchnummer", label: "Aktenzeichen" },
      ],
    },
    {
      key: "bank",
      railLabel: "Bankverbindung",
      label: "Bankverbindung & Steuer",
      description:
        "Bankverbindung (aus den Kundendaten übernommen) und Steuerstatus prüfen.",
      maxWidth: 820,
      fields: [
        ...BANK_FIELDS,
        {
          name: "vorsteuerabzugsberechtigt",
          label: "Ist der Versicherungsnehmer vorsteuerabzugsberechtigt?",
        },
      ],
    },
    {
      key: "abwicklung",
      railLabel: "Abwicklung",
      label: "Abwicklung & Kommunikation",
      description: "Legen Sie Kommunikation und Schriftverkehr fest.",
      maxWidth: 820,
      fields: [...COMM_FIELDS],
    },
    {
      key: "regulierung",
      railLabel: "Regulierung",
      label: "Regulierungsempfehlung",
      description: "Geben Sie Ihre Regulierungsempfehlung an den Versicherer.",
      maxWidth: 760,
      fields: [
        {
          name: "regulierung_empfehlung",
          label: "Empfehlung",
          options: [
            {
              key: "Regulierung nach Sach- und Rechtslage",
              label: "Regulierung nach Sach- und Rechtslage",
            },
            {
              key: "Regulierung in bestimmter Höhe",
              label: "Regulierung in bestimmter Höhe",
            },
          ],
        },
        "regulierung_betrag",
        "regulierung_auszahlung_an",
        { name: "regulierung_bemerkungen", label: "Zusätzliche Bemerkungen" },
      ],
    },
    RECAP_STEP,
  ],
};

/**
 * Human labels for `requiredNames` — fields a custom control manages, so they
 * have no descriptor of their own to take a label from. Used when telling the
 * broker which field is blocking "Weiter" / "An Versicherer senden"; without
 * these the UI would show the raw column name (e.g. "schaden_ursache").
 */
const REQUIRED_LABELS = {
  schaden_ursache: "Schadenart",
  schaden_ort: "Schadenort",
  fahrzeug_kennzeichen: "Kennzeichen",
};

/** Flatten a formdef's stepper children; index named descriptors, collect nameless. */
function buildDescriptorIndex(fdef) {
  const stepper = fdef?.formdefinition?.find((c) => c.control === "stepper");
  const byName = new Map();
  const nameless = [];
  (stepper?.steps || []).forEach((step) => {
    (step.children || []).forEach((child) => {
      if (child.name) byName.set(child.name, child);
      else nameless.push(child);
    });
  });
  return { byName, nameless };
}

/**
 * Give a formdef field the click-dummy look: yes/no switches become segmented
 * "nein/ja" buttons, and small static selects become segmented option buttons.
 * Everything else is returned unchanged. Descriptors come from a cloned fdef, so
 * returning a shallow copy is safe.
 */
function dummyize(desc) {
  if (desc.control === "switch") {
    return { ...desc, control: "segmented", boolean: true };
  }
  if (
    desc.control === "select" &&
    Array.isArray(desc.options) &&
    desc.options.length > 0 &&
    desc.options.length <= 7 &&
    !desc.get_api
  ) {
    return { ...desc, control: "segmented" };
  }
  return desc;
}

/** Build the concrete schmidt wizard step list for a formdef + sparte. */
export function buildClaimWizardSteps(fdef, sparte) {
  const { byName, nameless } = buildDescriptorIndex(fdef);
  const family = familyOf(sparte);

  // Broker templates may be a static array or a function of the reclassification
  // context (kfz / haftpflicht branch on Kasko / Betrieb).
  const brokerDef = BROKER_TEMPLATES[family] || BROKER_TEMPLATES.sach;
  const brokerSteps =
    typeof brokerDef === "function"
      ? brokerDef({ isKasko: isKasko(sparte), isBetrieb: isBetrieb(sparte) })
      : brokerDef;

  const template = [
    ...CUSTOMER_TEMPLATE.filter((s) => !s.family || s.family === family).map(
      (s) => ({ ...s, phase: "customer" })
    ),
    ...brokerSteps.map((s) => ({ ...s, phase: "broker" })),
  ];

  const used = new Set();
  const placedFields = []; // {name, label} for the recap

  const steps = template
    .map((spec) => {
      const children = [];
      (spec.fields || []).forEach((f) => {
        // String → pull the formdef descriptor by name.
        if (typeof f === "string") {
          const desc = byName.get(f);
          if (!desc || used.has(f)) return; // tolerant: skip missing / dup
          used.add(f);
          children.push(dummyize(desc));
          if (desc.label)
            placedFields.push({ name: f, label: desc.label, section: spec.label });
          return;
        }
        // Object with a name that exists in the formdef → field override: pull the
        // descriptor and shallow-merge the overrides (e.g. a relaxed `valid`).
        // The fdef is a deep clone, so mutating/copying descriptors is safe.
        if (f.name && byName.has(f.name)) {
          if (used.has(f.name)) return;
          used.add(f.name);
          const merged = dummyize({ ...byName.get(f.name), ...f });
          children.push(merged);
          if (merged.label)
            placedFields.push({
              name: f.name,
              label: merged.label,
              section: spec.label,
            });
          return;
        }
        // Otherwise an inline control or inline field (name not in the formdef).
        //
        // A spec that names a field but brings no `control` of its own is an
        // override for a formdef descriptor (e.g. `{name: "schaden_hoehe",
        // valid: …}`). If that descriptor is missing — an unmapped Sparte falls
        // back to the empty `fdefFormX`, and several formdefs simply lack some
        // broker fields — it is not an inline control: rendering it produced
        // "Unknown control: undefined". Skip it, exactly as the string branch
        // above skips a name it cannot resolve.
        if (f.name && !f.control) {
          if (import.meta.env?.DEV)
            // eslint-disable-next-line no-console
            console.warn(
              `[claimWizard] step "${spec.key}": no descriptor for "${f.name}" — field skipped`
            );
          return;
        }
        const ctrl = { cols: 12, ...f };
        if (ctrl.control === "claim_einordnung") ctrl.currentSparte = sparte;
        children.push(ctrl);
        if (ctrl.name && ctrl.label)
          placedFields.push({
            name: ctrl.name,
            label: ctrl.label,
            section: spec.label,
          });
      });
      return {
        key: spec.key,
        label: spec.label,
        railLabel: spec.railLabel,
        description: spec.description,
        maxWidth: spec.maxWidth,
        phase: spec.phase,
        requireCheck: spec.requireCheck,
        requiredNames: spec.requiredNames,
        requiredLabels: Object.fromEntries(
          (spec.requiredNames || []).map((n) => [
            n,
            REQUIRED_LABELS[n] || byName.get(n)?.label || n,
          ])
        ),
        children,
      };
    })
    // Drop steps that resolved to nothing (e.g. a family missing all its fields).
    .filter((s) => s.children.length > 0);

  // Give the recap control the placed fields grouped into sections by step (the
  // dummy's `fullRecap` groups fields under headers).
  const sections = [];
  const byTitle = new Map();
  placedFields.forEach((f) => {
    if (!byTitle.has(f.section)) {
      const s = { title: f.section, rows: [] };
      byTitle.set(f.section, s);
      sections.push(s);
    }
    byTitle.get(f.section).rows.push({ name: f.name, label: f.label });
  });
  steps.forEach((s) => {
    s.children.forEach((c) => {
      if (c.control === "claim_recap") {
        c.sections = sections;
        c.fields = placedFields; // fallback
      }
    });
  });

  // Leftover: every formdef field not placed by a template goes into a hidden
  // broker step so the flattened model / validation stays complete (never renders).
  const leftover = [];
  byName.forEach((desc, name) => {
    if (!used.has(name)) leftover.push(desc);
  });
  leftover.push(...nameless);
  if (leftover.length > 0) {
    steps.push({
      key: "_leftover",
      label: "Weitere Angaben",
      railLabel: "Weitere Angaben",
      phase: "broker",
      when: () => false, // never rendered; keeps the model complete
      children: leftover,
    });
  }

  return steps;
}

/**
 * Return the fdef with its stepper steps replaced by the schmidt wizard steps.
 * Mutates the passed fdef's stepper in place (callers pass a cloneDeep'd fdef).
 */
export function applyClaimWizardToFdef(fdef, sparte) {
  const stepper = fdef?.formdefinition?.find((c) => c.control === "stepper");
  if (stepper && Array.isArray(stepper.steps)) {
    stepper.steps = buildClaimWizardSteps(fdef, sparte);
    // The wizard shows only the stepper. Drop any leading top-level controls
    // (e.g. formI's Haftpflicht notice) which would otherwise render above the
    // phase banner — the Einordnung step surfaces that guidance itself.
    fdef.formdefinition = [stepper];
  }
  return fdef;
}
